"use client";

import React from "react";
import { Phone, Mail, MapPin, CircleCheck, ArrowRight, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const ContactForm = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center bg-secondary/30 rounded-lg border border-border animate-in fade-in zoom-in duration-300">
        <div className="size-12 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
          <CircleCheck className="size-6 text-green-500" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Message Sent!</h3>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 hover:text-primary">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Your Name <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="bg-secondary/50 border-border/50 focus-visible:border-primary/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Phone Number <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(02) 1234 5678" {...field} className="bg-secondary/50 border-border/50 focus-visible:border-primary/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Email Address <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="bg-secondary/50 border-border/50 focus-visible:border-primary/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Company (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" {...field} className="bg-secondary/50 border-border/50 focus-visible:border-primary/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="service_interest" rules={{
      required: "Please select a service"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Service of Interest <span className="text-primary">*</span></FormLabel>
            <FormControl>
              <div className="relative">
                <select className="flex h-9 w-full items-center justify-between rounded-md border border-border/50 bg-secondary/50 px-3 py-2 text-sm shadow-xs ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 text-foreground appearance-none" {...field} value={field.value || ""}>
                  <option value="" disabled>Select a service</option>
                  <option value="Office Cleaning">Office Cleaning</option>
                  <option value="Strata Building Cleaning">Strata Building Cleaning</option>
                  <option value="Gym Cleaning">Gym Cleaning</option>
                  <option value="Childcare Centre Cleaning">Childcare Centre Cleaning</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                  <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Message <span className="text-primary">*</span></FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your cleaning needs..." className="min-h-[100px] bg-secondary/50 border-border/50 focus-visible:border-primary/50 resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-11">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Sending Request...
          </> : <>
            Get a Free Quote <ArrowRight className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
};
export default function ContactUs() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="contact-us" className="dark bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] bg-secondary/20 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`grid lg:grid-cols-12 gap-12 items-start transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                Looking for Reliable <span className="text-primary">Commercial Cleaning?</span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Get in touch with Gold Star Executive Cleaning today. We provide premium cleaning solutions for offices, strata buildings, and commercial spaces across NSW.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4 p-4 rounded-lg bg-secondary/30 border border-border/50 hover:border-primary/30 transition-colors">
                <div className="bg-primary/10 p-3 rounded-full shrink-0">
                  <Phone className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Call Us Directly</h3>
                  <p className="text-muted-foreground text-sm mb-2">Mon-Fri: 8am – 6pm</p>
                  <Link to="tel:0412922689" className="text-lg font-bold text-primary hover:underline">
                    0412 922 689
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-lg bg-secondary/30 border border-border/50 hover:border-primary/30 transition-colors">
                <div className="bg-primary/10 p-3 rounded-full shrink-0">
                  <Mail className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Email Us</h3>
                  <p className="text-muted-foreground text-sm mb-2">For quotes and inquiries</p>
                  <Link to="mailto:cleaningwithgoldstar@gmail.com" className="text-base font-medium text-foreground hover:text-primary transition-colors">
                    cleaningwithgoldstar@gmail.com
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-lg bg-secondary/30 border border-border/50 hover:border-primary/30 transition-colors">
                <div className="bg-primary/10 p-3 rounded-full shrink-0">
                  <MapPin className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Service Areas</h3>
                  <p className="text-muted-foreground text-sm">
                    Proudly servicing Newcastle, Central Coast, and Sydney regions.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="bg-card border border-border rounded-xl shadow-xl p-6 md:p-8">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-foreground">Request a Quote</h3>
                <p className="text-muted-foreground text-sm mt-1">
                  Fill out the form below and we'll get back to you within 24 hours.
                </p>
              </div>
              
              <FormProvider formId="contact_form_services" sectionName="contact-us-section" defaultValues={{
              name: "",
              email: "",
              phone: "",
              company: "",
              service_interest: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              service_interest: {
                required: true
              },
              message: {
                required: true
              }
            }} submitText="Get a Free Quote" successMessage="Thank you! Your quote request has been received. We will contact you shortly.">
                <ContactForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="fixed bottom-6 right-6 z-50 md:hidden">
        <Link to="tel:0412922689">
          <Button size="icon" className="size-14 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/20 hover:bg-primary/90 animate-bounce" aria-label="Call Now">
            <Phone className="size-6 fill-current" />
          </Button>
        </Link>
      </div>
    </section>;
}